import React, { useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import {
    ArrowUpTrayIcon,
    CheckIcon,
    DocumentTextIcon,
    PlusIcon,
    XMarkIcon,
    ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { challengeApi, errorMessage } from './api';
import { fileSize, openFile, useFileUrl } from './ui';

const inputClass = 'w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20 disabled:bg-ink-50 disabled:text-ink-600';

const ACCEPT = {
    image: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'] },
    image_pdf: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'], 'application/pdf': ['.pdf'] }
};

export function NaButton({ active, onClick, disabled }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={active}
            className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${active
                ? 'border-ink-800 bg-ink-800 text-white'
                : 'border-border bg-white text-ink-700 hover:border-ink-400'} disabled:cursor-not-allowed`}
        >
            {active ? <span className="inline-flex items-center gap-1">N/A <CheckIcon className="h-3.5 w-3.5" /></span> : 'N/A'}
        </button>
    );
}

export function MentorComments({ comments }) {
    if (!comments || !comments.length) return null;
    return (
        <div className="mt-3 space-y-2">
            {comments.map((c) => (
                <div key={c.id} className="flex gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-950">
                    <ChatBubbleLeftEllipsisIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" aria-hidden="true" />
                    <p><span className="font-semibold">Mentor comment:</span> {c.text}</p>
                </div>
            ))}
        </div>
    );
}

function FieldLabel({ field, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="block text-sm font-semibold text-ink-900">
            {field.number ? <span className="mr-1.5 text-brand-700">{field.number}</span> : null}
            {field.label}
        </label>
    );
}

/**
 * One question. `boxed` gives each question its own bordered card (workbook);
 * otherwise questions stack under a plain label (single-page challenges).
 */
export default function FieldRenderer({
    field, answer, onChange, readOnly, files, onFilesChange, challengeId, boxed, invalid, comments
}) {
    const a = answer || {};
    const id = `f-${field.key}`;
    const na = Boolean(field.allow_na && a.na);
    const set = (patch) => onChange({ ...a, ...patch });

    if (field.type === 'score') {
        return <ScoreField field={field} answer={a} set={set} readOnly={readOnly} invalid={invalid} comments={comments} />;
    }
    if (field.type === 'checkbox') {
        return <CheckboxField field={field} answer={a} set={set} readOnly={readOnly} invalid={invalid} comments={comments} />;
    }
    if (field.type === 'checklist') {
        return <ChecklistField field={field} answer={a} set={set} readOnly={readOnly} invalid={invalid} comments={comments} />;
    }

    const wrapper = boxed
        ? `rounded-xl border p-4 md:p-5 transition-colors ${na ? 'border-ink-200 bg-ink-50' : invalid ? 'border-red-300 bg-white' : 'border-border bg-white'}`
        : `${invalid ? 'rounded-xl ring-2 ring-red-200 ring-offset-4' : ''}`;

    return (
        <div className={wrapper} data-field={field.key}>
            {field.type !== 'file_upload' || boxed ? (
                <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <FieldLabel field={field} htmlFor={id} />
                        {field.help && !na && field.type !== 'file_upload' && field.type !== 'process_steps' && <p className="mt-1 text-xs text-ink-500">{field.help}</p>}
                    </div>
                    {field.allow_na && <NaButton active={na} disabled={readOnly} onClick={() => set({ na: !na })} />}
                </div>
            ) : null}

            {na ? (
                <p className="text-xs text-ink-500">
                    Marked <span className="font-semibold text-ink-700">Not Applicable</span> — this field is skipped and won't count as missing.
                </p>
            ) : (
                <FieldInput
                    field={field} a={a} set={set} id={id} readOnly={readOnly}
                    files={files} onFilesChange={onFilesChange} challengeId={challengeId}
                />
            )}
            <MentorComments comments={comments} />
        </div>
    );
}

function FieldInput({ field, a, set, id, readOnly, files, onFilesChange, challengeId }) {
    switch (field.type) {
        case 'short_text':
            return <input id={id} className={inputClass} value={a.value || ''} placeholder={field.placeholder || ''} disabled={readOnly} onChange={(e) => set({ value: e.target.value })} />;
        case 'long_text':
            return (
                <textarea
                    id={id} className={`${inputClass} min-h-[72px] resize-y`} rows={field.rows || 3}
                    value={a.value || ''} placeholder={field.placeholder || ''} disabled={readOnly}
                    onChange={(e) => set({ value: e.target.value })}
                />
            );
        case 'number':
            return (
                <input
                    id={id} type="number" inputMode="numeric" className={inputClass}
                    value={a.value === undefined || a.value === null ? '' : a.value}
                    min={field.min ?? undefined} max={field.max ?? undefined} disabled={readOnly}
                    onChange={(e) => set({ value: e.target.value === '' ? '' : Number(e.target.value) })}
                />
            );
        case 'date':
            return <input id={id} type="date" className={inputClass} value={a.value || ''} disabled={readOnly} onChange={(e) => set({ value: e.target.value })} />;
        case 'single_choice':
            return <ChoiceInput field={field} a={a} set={set} readOnly={readOnly} />;
        case 'process_steps':
            return <ProcessSteps field={field} a={a} set={set} readOnly={readOnly} />;
        case 'file_upload':
            return <FileUpload field={field} files={files || []} onFilesChange={onFilesChange} readOnly={readOnly} challengeId={challengeId} />;
        default:
            return null;
    }
}

function ChoiceInput({ field, a, set, readOnly }) {
    const cols = { 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'sm:grid-cols-2 lg:grid-cols-4' }[field.columns] || 'md:grid-cols-2';
    return (
        <div role="radiogroup" aria-label={field.label} className={`grid grid-cols-1 gap-3 ${cols}`}>
            {(field.options || []).map((opt) => {
                const selected = a.value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={readOnly}
                        onClick={() => set({ value: opt.value })}
                        className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors disabled:cursor-default ${selected
                            ? 'border-brand-600 bg-brand-50/70 ring-1 ring-brand-600'
                            : 'border-border bg-white hover:border-ink-300'}`}
                    >
                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-brand-700' : 'border-ink-300'}`}>
                            {selected && <span className="h-2 w-2 rounded-full bg-brand-700" />}
                        </span>
                        <span className="min-w-0">
                            <span className="block text-sm font-semibold text-ink-900">{opt.label}</span>
                            {opt.description && <span className="mt-0.5 block text-xs leading-relaxed text-ink-500">{opt.description}</span>}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

function ScoreField({ field, answer: a, set, readOnly, invalid, comments }) {
    const na = Boolean(field.allow_na && a.na);
    const levels = field.levels || {};
    return (
        <div className={`rounded-xl border bg-white p-4 md:p-5 ${invalid ? 'border-red-300' : 'border-border'}`} data-field={field.key}>
            <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                <h3 className="text-[15px] font-semibold text-ink-900">{field.label}</h3>
                <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={`${field.label} score`}>
                    {[1, 2, 3, 4, 5].map((n) => {
                        const on = !na && a.score === n;
                        return (
                            <button
                                key={n} type="button" role="radio" aria-checked={on} disabled={readOnly}
                                onClick={() => set({ score: n, na: false })}
                                className={`h-10 w-10 rounded-lg border text-sm font-semibold transition-colors ${on
                                    ? 'border-brand-700 bg-brand-700 text-white'
                                    : 'border-border bg-white text-ink-800 hover:border-ink-400'} disabled:cursor-default`}
                            >
                                {n}
                            </button>
                        );
                    })}
                    {field.allow_na && (
                        <button
                            type="button" aria-pressed={na} disabled={readOnly}
                            onClick={() => set({ na: !na, score: null })}
                            className={`h-10 rounded-lg border px-3 text-xs font-semibold transition-colors ${na
                                ? 'border-ink-800 bg-ink-800 text-white'
                                : 'border-border bg-white text-ink-700 hover:border-ink-400'} disabled:cursor-default`}
                        >
                            {na ? 'N/A ✓' : 'N/A'}
                        </button>
                    )}
                </div>
            </div>
            {!na && (levels[1] || levels[3] || levels[5]) && (
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {[1, 3, 5].filter((n) => levels[n]).map((n) => {
                        const [head, ...rest] = String(levels[n]).split('—');
                        return (
                            <p key={n} className="rounded-lg bg-ink-50 px-3 py-2 text-xs leading-relaxed text-ink-600">
                                <span className="font-semibold text-ink-900">{n} {head.trim()}</span>
                                {rest.length ? ` — ${rest.join('—').trim()}` : ''}
                            </p>
                        );
                    })}
                </div>
            )}
            <label htmlFor={`f-${field.key}-reason`} className="mt-3 block text-xs text-ink-600">
                <span className="text-red-600">*</span> {na ? 'Why is this area not applicable?' : 'Evidence / reason'}
            </label>
            <textarea
                id={`f-${field.key}-reason`} rows={2} disabled={readOnly}
                className={`${inputClass} mt-1 resize-y`}
                value={a.reason || ''} onChange={(e) => set({ reason: e.target.value })}
            />
            <MentorComments comments={comments} />
        </div>
    );
}

function CheckboxField({ field, answer: a, set, readOnly, invalid, comments }) {
    const checked = a.value === true;
    const banner = field.display === 'banner';
    return (
        <div data-field={field.key}>
            <label className={`flex cursor-pointer items-center gap-3 ${banner
                ? `rounded-xl border px-4 py-3.5 ${checked ? 'border-brand-200 bg-brand-50/60' : invalid ? 'border-red-300 bg-white' : 'border-border bg-white'}`
                : ''} ${readOnly ? 'cursor-default' : ''}`}
            >
                <input
                    type="checkbox" checked={checked} disabled={readOnly}
                    onChange={(e) => set({ value: e.target.checked })}
                    className="h-5 w-5 rounded border-ink-300 text-brand-700 focus:ring-brand-600"
                />
                <span className="text-sm">
                    <span className="font-semibold text-ink-900">{field.label}</span>
                    {field.help && <span className="text-ink-500"> — {field.help}</span>}
                </span>
            </label>
            <MentorComments comments={comments} />
        </div>
    );
}

function ChecklistField({ field, answer: a, set, readOnly, invalid, comments }) {
    const items = field.items || [];
    const checked = new Set(a.checked || []);
    const toggle = (i) => {
        const next = new Set(checked);
        if (next.has(i)) next.delete(i); else next.add(i);
        set({ checked: [...next].sort((x, y) => x - y) });
    };
    return (
        <div data-field={field.key} className={invalid ? 'rounded-xl ring-2 ring-red-200 ring-offset-4' : ''}>
            <ul className="grid grid-cols-1 gap-x-8 gap-y-2.5 md:grid-cols-2">
                {items.map((item, i) => (
                    <li key={i}>
                        <label className={`flex items-start gap-2.5 text-sm text-ink-800 ${readOnly ? '' : 'cursor-pointer'}`}>
                            <input
                                type="checkbox" checked={checked.has(i)} disabled={readOnly} onChange={() => toggle(i)}
                                className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-700 focus:ring-brand-600"
                            />
                            <span>{item}</span>
                        </label>
                    </li>
                ))}
            </ul>
            <MentorComments comments={comments} />
        </div>
    );
}

function ProcessSteps({ field, a, set, readOnly }) {
    const steps = Array.isArray(a.steps) ? a.steps : [];
    const dragFrom = useRef(null);
    const [over, setOver] = useState(null);
    const listRef = useRef(null);

    const update = (next) => set({ steps: next });
    const addStep = () => {
        update([...steps, '']);
        setTimeout(() => {
            const inputs = listRef.current?.querySelectorAll('input[data-step]');
            inputs?.[inputs.length - 1]?.focus();
        }, 0);
    };
    const move = (from, to) => {
        if (from === null || to === null || from === to) return;
        const next = [...steps];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        update(next);
    };

    const Arrow = () => <span className="text-ink-400" aria-hidden="true">→</span>;

    return (
        <div>
            <div ref={listRef} className="flex flex-wrap items-center gap-2.5 rounded-xl bg-ink-50 p-3.5">
                <span className="rounded-full bg-ink-950 px-3.5 py-1.5 text-xs font-bold tracking-wide text-white">START</span>
                {steps.map((step, i) => (
                    <React.Fragment key={i}>
                        <Arrow />
                        <div
                            draggable={!readOnly}
                            onDragStart={() => { dragFrom.current = i; }}
                            onDragOver={(e) => { e.preventDefault(); setOver(i); }}
                            onDragLeave={() => setOver(null)}
                            onDrop={(e) => { e.preventDefault(); move(dragFrom.current, i); dragFrom.current = null; setOver(null); }}
                            className={`flex items-center gap-2 rounded-lg border bg-white py-1.5 pl-2.5 pr-1.5 ${over === i ? 'border-brand-600' : 'border-border'} ${readOnly ? '' : 'cursor-grab'}`}
                        >
                            <span className="text-xs font-semibold text-brand-700">{i + 1}</span>
                            <input
                                data-step
                                value={step}
                                disabled={readOnly}
                                aria-label={`Step ${i + 1}`}
                                placeholder="Step name"
                                onChange={(e) => update(steps.map((s, j) => (j === i ? e.target.value : s)))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addStep(); } }}
                                className="w-40 border-0 bg-transparent p-0 text-sm text-ink-900 focus:outline-none focus:ring-0 disabled:text-ink-700"
                            />
                            {!readOnly && (
                                <button
                                    type="button" aria-label={`Remove step ${i + 1}`}
                                    onClick={() => update(steps.filter((_, j) => j !== i))}
                                    className="rounded-md border border-border p-1 text-ink-500 hover:text-red-600"
                                >
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </React.Fragment>
                ))}
                {!readOnly && (
                    <>
                        <Arrow />
                        <button type="button" onClick={addStep} className="rounded-lg border border-border bg-white px-3.5 py-2 text-xs font-semibold text-ink-900 hover:border-ink-400">
                            + Add step
                        </button>
                    </>
                )}
                <Arrow />
                <span className="rounded-full bg-emerald-50 px-3.5 py-1.5 text-xs font-bold tracking-wide text-emerald-800">OUTCOME</span>
            </div>
            <label htmlFor={`f-${field.key}-outcome`} className="mt-4 block text-sm font-semibold text-ink-900">Outcome</label>
            <input
                id={`f-${field.key}-outcome`} className={`${inputClass} mt-1.5`} disabled={readOnly}
                placeholder="e.g. Customer receives a quote"
                value={a.outcome || ''} onChange={(e) => set({ outcome: e.target.value })}
            />
            {field.help && <p className="mt-2 text-xs text-ink-500">{field.help}</p>}
        </div>
    );
}

function FileUpload({ field, files, onFilesChange, readOnly, challengeId }) {
    const [uploading, setUploading] = useState(0);
    const maxFiles = Number(field.max_files) || 5;
    const perFile = field.consent_scope !== 'all';
    const imagesOnly = field.accept === 'image';
    const room = maxFiles - files.length;

    const onDrop = async (accepted, rejected) => {
        if (rejected.length) toast.error(rejected[0].errors?.[0]?.code === 'file-too-large' ? 'File is too large (max 5 MB)' : (imagesOnly ? 'Upload a JPG or PNG image' : 'Upload a JPG, PNG or PDF file'));
        const batch = accepted.slice(0, Math.max(0, room));
        if (accepted.length > batch.length) toast.error(`You can upload up to ${maxFiles} file${maxFiles === 1 ? '' : 's'} here`);
        for (const file of batch) {
            setUploading((n) => n + 1);
            try {
                const saved = await challengeApi.upload(challengeId, field.key, file);
                onFilesChange((list) => [...list, saved]);
            } catch (error) {
                toast.error(errorMessage(error, 'Upload failed'));
            } finally {
                setUploading((n) => n - 1);
            }
        }
    };

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: ACCEPT[field.accept] || ACCEPT.image_pdf,
        maxSize: 5 * 1024 * 1024,
        noClick: true,
        disabled: readOnly || room <= 0
    });

    const remove = async (file) => {
        try {
            await challengeApi.deleteFile(file.id);
            onFilesChange((list) => list.filter((f) => f.id !== file.id));
        } catch (error) {
            toast.error(errorMessage(error, 'Could not remove the file'));
        }
    };

    const setConsent = async (file, consent) => {
        try {
            if (perFile) {
                const saved = await challengeApi.setFileConsent(file.id, consent);
                onFilesChange((list) => list.map((f) => (f.id === saved.id ? saved : f)));
            } else {
                const saved = await challengeApi.setFieldConsent(challengeId, field.key, consent);
                onFilesChange((list) => [...list.filter((f) => f.field_key !== field.key), ...saved.filter((f) => f.field_key === field.key)]);
            }
        } catch (error) {
            toast.error(errorMessage(error, 'Could not update permission'));
        }
    };

    const hint = `${imagesOnly ? 'JPG or PNG' : 'JPG, PNG, PDF'} · max 5 MB`;
    const allConsented = files.length > 0 && files.every((f) => f.consent);

    if (perFile) {
        return (
            <div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                    {files.map((file) => (
                        <FileTile key={file.id} file={file} readOnly={readOnly} onRemove={() => remove(file)}>
                            {field.consent_label && (
                                <label className="mt-2 flex items-start gap-2 text-xs text-ink-700">
                                    <input
                                        type="checkbox" checked={file.consent} disabled={readOnly}
                                        onChange={(e) => setConsent(file, e.target.checked)}
                                        className="mt-px h-4 w-4 rounded border-ink-300 text-brand-700 focus:ring-brand-600"
                                    />
                                    <span>{field.consent_label}</span>
                                </label>
                            )}
                        </FileTile>
                    ))}
                    {!readOnly && room > 0 && (
                        <div
                            {...getRootProps()}
                            className={`flex min-h-[180px] flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 text-center transition-colors ${isDragActive ? 'border-brand-600 bg-brand-50' : 'border-ink-200 bg-ink-50/60'}`}
                        >
                            <input {...getInputProps()} />
                            <button type="button" onClick={open} className="flex flex-col items-center gap-1 text-brand-700">
                                <PlusIcon className="h-6 w-6" />
                                <span className="text-sm font-semibold">{uploading ? 'Uploading…' : 'Add file'}</span>
                            </button>
                            <span className="mt-1 text-xs text-ink-500">{hint}</span>
                        </div>
                    )}
                </div>
                {field.help && <p className="mt-3 text-xs text-ink-500">{field.help}</p>}
            </div>
        );
    }

    return (
        <div>
            {!readOnly && room > 0 && (
                <div
                    {...getRootProps()}
                    className={`flex items-center gap-4 rounded-xl border-2 border-dashed px-5 py-4 transition-colors ${isDragActive ? 'border-brand-600 bg-brand-50' : 'border-ink-200 bg-ink-50/60'}`}
                >
                    <input {...getInputProps()} />
                    <ArrowUpTrayIcon className="h-6 w-6 shrink-0 text-brand-700" aria-hidden="true" />
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink-900">
                            {uploading ? 'Uploading…' : `Drag a ${imagesOnly ? 'photo' : 'file'} here or`}
                            <button type="button" onClick={open} className="rounded-lg border border-border bg-white px-3 py-1 text-xs font-semibold text-ink-900 hover:border-ink-400">
                                Browse
                            </button>
                        </div>
                        <p className="mt-1 text-xs text-ink-500">{hint} · only you, your mentor and admins can see it</p>
                    </div>
                </div>
            )}
            {files.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                    {files.map((file) => <FileTile key={file.id} file={file} readOnly={readOnly} onRemove={() => remove(file)} />)}
                </div>
            )}
            {field.consent_label && (
                <label className={`mt-3 flex items-center gap-2.5 text-sm text-ink-800 ${files.length === 0 ? 'opacity-70' : ''}`}>
                    <input
                        type="checkbox" checked={allConsented} disabled={readOnly || files.length === 0}
                        onChange={(e) => setConsent(null, e.target.checked)}
                        className="h-4 w-4 rounded border-ink-300 text-brand-700 focus:ring-brand-600"
                    />
                    <span>
                        {field.consent_label}
                        {field.consent_hint && <span className="text-xs text-ink-500"> {field.consent_hint}</span>}
                    </span>
                </label>
            )}
        </div>
    );
}

export function FileTile({ file, readOnly, onRemove, children }) {
    const isImage = String(file.mime_type || '').startsWith('image/');
    const url = useFileUrl(file.id, isImage);
    return (
        <div className="overflow-hidden rounded-xl border border-border bg-white">
            <button
                type="button" onClick={() => openFile(file.id).catch(() => toast.error('Could not open the file'))}
                className="flex h-28 w-full items-center justify-center bg-ink-50"
                style={!url ? { backgroundImage: 'repeating-linear-gradient(135deg, #eef0f0 0 10px, #f7f8f8 10px 20px)' } : undefined}
                aria-label={`Open ${file.name}`}
            >
                {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <DocumentTextIcon className="h-8 w-8 text-ink-400" />}
            </button>
            <div className="p-2.5">
                <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-xs font-semibold text-ink-900" title={file.name}>{file.name}</p>
                    {!readOnly && onRemove && (
                        <button type="button" onClick={onRemove} aria-label={`Remove ${file.name}`} className="text-ink-400 hover:text-red-600">
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
                <p className="text-[11px] text-ink-500">{fileSize(file.size)}</p>
                {children}
            </div>
        </div>
    );
}
